import { useState, type FormEvent } from 'react';
import {
  useCoverage,
  useEvents,
  useInvitations,
  useMetrics,
  useOperations,
  useProjects,
  useRecordEvent,
  useRetryAnalysisJob,
  useSetInvitation,
} from '../../api/queries';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../../api/client';
import type {
  Invitation,
  JobState,
  MetricsReport,
  OperationalEvent,
  OperationalEventType,
  OperationsResponse,
  Outcome,
  Role,
  Visibility,
} from '../../api/types';
import { useSession } from '../../auth/session';
import { Field, MutationError } from '../../components/Form';
import { ErrorState, Loading, QueryState } from '../../components/States';
import {
  buildPayload,
  eventOrder,
  eventSpecs,
  fieldLabel,
  localToIso,
  newReference,
  stages,
  type EventDraft,
  type EventField,
  type ReferenceField,
} from '../../lib/events';
import { formatDate, formatDateTime, outcomeLabel, roleLabel } from '../../lib/format';
import { StewardNav } from './StewardNav';

const OUTCOMES: Outcome[] = ['positive', 'negative', 'unknown'];

const nowLocal = () => new Date().toISOString().slice(0, 16);
const truncate = (text: string, size: number) => (text.length > size ? `${text.slice(0, size - 1)}…` : text);
/** `ambiguousFacts` → "Ambiguous facts". */
const humanise = (key: string) => {
  const words = key.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

function referenceOf(event: OperationalEvent): { field: ReferenceField; value: string } | null {
  if (event.payload.supportId !== undefined) return { field: 'supportId', value: event.payload.supportId };
  if (event.payload.actionId !== undefined) return { field: 'actionId', value: event.payload.actionId };
  return null;
}

function rootLabel(event: OperationalEvent, projects: Map<string, string>): string {
  const note = event.payload.note?.trim();
  const project = event.projectId ? projects.get(event.projectId) : undefined;
  return `${formatDate(event.occurredAt)} — ${note ? truncate(note, 72) : 'Note withheld by attribution preference'}${project ? ` · ${project}` : ''}`;
}

const isRoot = (event: OperationalEvent) => event.type === 'support_requested' || event.type === 'retrospective_action';

function rootFor(event: OperationalEvent, events: OperationalEvent[]): OperationalEvent | undefined {
  const reference = referenceOf(event);
  if (reference === null || isRoot(event)) return undefined;
  return events.find(
    (candidate) =>
      isRoot(candidate) && candidate.payload[reference.field] === reference.value && (candidate.projectId ?? null) === (event.projectId ?? null),
  );
}

interface Chain {
  root: OperationalEvent;
  followups: OperationalEvent[];
}

function chainsOf(events: OperationalEvent[]): Chain[] {
  return events
    .filter(isRoot)
    .map((root) => {
      const reference = referenceOf(root);
      return {
        root,
        followups: events.filter(
          (event) =>
            event.id !== root.id &&
            reference !== null &&
            event.payload[reference.field] === reference.value &&
            (event.projectId ?? null) === (root.projectId ?? null),
        ),
      };
    })
    .sort((a, b) => b.root.occurredAt.localeCompare(a.root.occurredAt));
}

/** States follow the measurement guide: silence, pending and unknown are never shown as failure. */
function chainStates(chain: Chain, now: number): { text: string; tone: 'ok' | 'warn' | 'neutral' }[] {
  const find = (type: OperationalEventType) => chain.followups.find((event) => event.type === type);
  if (chain.root.type === 'retrospective_action') {
    const outcome = find('action_outcome');
    if (outcome === undefined) return [{ text: 'No outcome recorded yet', tone: 'neutral' }];
    if (outcome.payload.outcome === 'positive') return [{ text: 'Reported: happened', tone: 'ok' }];
    if (outcome.payload.outcome === 'negative') return [{ text: 'Reported: did not happen', tone: 'neutral' }];
    return [{ text: 'Outcome not known', tone: 'warn' }];
  }
  const states: { text: string; tone: 'ok' | 'warn' | 'neutral' }[] = [];
  const window = find('support_window_accepted');
  const response = find('support_response');
  const acknowledgement = find('useful_help_acknowledged');
  if (window === undefined) states.push({ text: 'Awaiting explicit acceptance', tone: 'neutral' });
  else if (response === undefined) {
    const end = window.payload.windowEnd === undefined ? Number.NaN : Date.parse(window.payload.windowEnd);
    if (Number.isNaN(end)) states.push({ text: 'Accepted; window not recorded', tone: 'warn' });
    else if (end > now) states.push({ text: `Pending — window open until ${formatDateTime(window.payload.windowEnd)}`, tone: 'warn' });
    else states.push({ text: 'No response recorded within the window (not the same as a refusal)', tone: 'neutral' });
  }
  if (response !== undefined) states.push({ text: 'Response recorded', tone: 'ok' });
  if (acknowledgement !== undefined) {
    if (acknowledgement.payload.outcome === 'positive') states.push({ text: 'Recipient: useful', tone: 'ok' });
    else if (acknowledgement.payload.outcome === 'negative') states.push({ text: 'Recipient: not useful', tone: 'neutral' });
    else states.push({ text: 'Usefulness not known', tone: 'warn' });
  } else if (response !== undefined) states.push({ text: 'Usefulness not asked yet', tone: 'warn' });
  return states;
}

function MeasurementStatus({ events }: { events: OperationalEvent[] }) {
  const coverage = useCoverage();
  const started = events.filter((event) => event.type === 'measurement_started').sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))[0];
  const prospective = coverage.data?.historical?.prospective;
  return (
    <section className="panel" aria-labelledby="measurement-title">
      <h2 id="measurement-title" className="panel__title">
        Prospective measurement
      </h2>
      {started === undefined ? (
        <p className="notice notice--proposed">
          Measurement has <strong>not started</strong>
          {prospective?.status !== undefined ? ` (recorded state: ${prospective.status.replace(/_/g, ' ')})` : ''}. Until a steward records an
          explicit start, nothing counts as zero, and a missing record is not a negative outcome.
        </p>
      ) : (
        <p>
          Started {formatDateTime(started.occurredAt)}. {started.payload.note}
        </p>
      )}
    </section>
  );
}

function Chips({ values, label, warn = false }: { values: Record<string, number>; label: string; warn?: boolean }) {
  const entries = Object.entries(values).filter(([, count]) => count > 0);
  if (entries.length === 0) return null;
  return (
    <ul className="chips" aria-label={label}>
      {entries.map(([key, count]) => (
        <li key={key} className={warn ? 'chip chip--warn' : 'chip'}>
          {humanise(key)} {count}
        </li>
      ))}
    </ul>
  );
}

/** Measurement-package flags that explain why a value is withheld or partial. */
const OBSERVATION_REASONS: Record<string, string> = {
  measurementStartUnknown: 'Measurement start unknown',
  periodNotFullyObserved: 'Period not fully observed yet',
  periodWhollyBeforeMeasurementStart: 'Period is entirely before measurement start',
  observationWhollyBeforeMeasurementStart: 'Observations end before measurement start',
  periodPartiallyBeforeMeasurementStart: 'Period begins before measurement start',
};

function ReasonChips({ values, label }: { values: Record<string, number>; label: string }) {
  const reasons = Object.entries(values).filter(([key, flag]) => key in OBSERVATION_REASONS && flag > 0);
  if (reasons.length === 0) return null;
  return (
    <ul className="chips" aria-label={label}>
      {reasons.map(([key]) => (
        <li key={key} className="chip chip--warn">
          {OBSERVATION_REASONS[key]}
        </li>
      ))}
    </ul>
  );
}

const withoutReasons = (values: Record<string, number>) => Object.fromEntries(Object.entries(values).filter(([key]) => !(key in OBSERVATION_REASONS)));

export function observationSentence(report: MetricsReport): string {
  if (report.measurementStart === null) return 'Measurement start is unknown, so values are withheld rather than shown as zero.';
  const started = `Measurement started ${formatDateTime(report.measurementStart)}.`;
  const effective = report.effectiveObservationPeriod;
  if (effective === null) return `${started} No part of this period after that start is observable yet, so values are withheld rather than shown as zero.`;
  if (effective === undefined) return started;
  return `${started} Values cover ${formatDateTime(effective.from)} to ${formatDateTime(effective.to)} (end excluded).`;
}

function MetricsView({ report }: { report: MetricsReport }) {
  const quality = Object.entries(report.quality).filter(([, count]) => count > 0);
  return (
    <>
      <p className="small muted">
        Period {formatDate(report.period.from)} to {formatDate(report.period.to)} (end excluded); follow-ups counted to{' '}
        {formatDateTime(report.asOf)}. {observationSentence(report)}
      </p>
      {report.metrics.length === 0 ? (
        <p className="muted">No measures are defined for this period.</p>
      ) : (
        <ul className="metric-list">
          {report.metrics.map((metric) => (
            <li key={metric.id} className="metric">
              <p className="metric__label">{metric.label}</p>
              <p className="metric__value">
                {metric.value === null ? 'Not measured' : metric.value.toLocaleString('en-GB')}{' '}
                {metric.value !== null && <span className="metric__unit">{metric.unit}</span>}
              </p>
              <p className="small muted">
                A count, not a rate{metric.denominator > 0 ? `; population of ${metric.denominator.toLocaleString('en-GB')}` : ''}. {metric.population}
              </p>
              <ReasonChips values={metric.missingness} label={`Why ${metric.label} is withheld or partial`} />
              <Chips values={withoutReasons(metric.missingness)} label={`Unknown, absent or censored for ${metric.label}`} warn />
              <Chips values={metric.breakdown} label={`Breakdown for ${metric.label}`} />
            </li>
          ))}
        </ul>
      )}
      {quality.length > 0 && (
        <>
          <p className="minor-title">Data quality</p>
          <ul className="point-list">
            {quality.map(([key, count]) => (
              <li key={key}>
                {humanise(key)}: {count}
              </li>
            ))}
          </ul>
        </>
      )}
      {report.limitations.length > 0 && (
        <ul className="point-list point-list--limits">
          {report.limitations.map((limit) => (
            <li key={limit}>{limit}</li>
          ))}
        </ul>
      )}
    </>
  );
}

function Metrics() {
  const metrics = useMetrics();
  return (
    <section className="panel" aria-labelledby="metrics-title">
      <h2 id="metrics-title" className="panel__title">
        Measures
      </h2>
      <QueryState query={metrics} label="measures">
        {(report) => <MetricsView report={report} />}
      </QueryState>
    </section>
  );
}

function FollowUps({ events, projects }: { events: OperationalEvent[]; projects: Map<string, string> }) {
  const chains = chainsOf(events);
  const now = Date.now();
  return (
    <section className="panel" aria-labelledby="followups-title">
      <h2 id="followups-title" className="panel__title">
        Support requests and actions
      </h2>
      {chains.length === 0 ? (
        <p className="muted">No support requests or retrospective actions have been recorded.</p>
      ) : (
        <ul className="chains">
          {chains.map((chain) => (
            <li key={chain.root.id} className="chain">
              <p className="chain__title">
                <span className="tag">{eventSpecs[chain.root.type].label}</span> {rootLabel(chain.root, projects)}
              </p>
              <ul className="chips" aria-label="Current state">
                {chainStates(chain, now).map((state) => (
                  <li key={state.text} className={state.tone === 'warn' ? 'chip chip--warn' : state.tone === 'ok' ? 'chip chip--ok' : 'chip'}>
                    {state.text}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

type FormErrors = Partial<Record<EventField | 'project' | 'occurredAt' | 'root', string>>;

function EventForm({ events, projects, truncated }: { events: OperationalEvent[]; projects: Map<string, string>; truncated: boolean }) {
  const session = useSession();
  const projectList = useProjects();
  const record = useRecordEvent();
  const types = eventOrder.filter((type) => !eventSpecs[type].stewardOnly || session.hasRole('steward'));
  // Links such as /steward/operations?event=project_update&project=<id> (from the pilots page) preselect the form.
  const [params] = useSearchParams();
  const requestedType = params.get('event');
  const requestedProject = params.get('project');
  const [type, setType] = useState<OperationalEventType>(() =>
    requestedType !== null && (types as string[]).includes(requestedType) ? (requestedType as OperationalEventType) : 'support_requested',
  );
  const [draft, setDraft] = useState<EventDraft>({});
  const [rootId, setRootId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [occurredAt, setOccurredAt] = useState(nowLocal());
  const [visibility, setVisibility] = useState<Visibility>('stewards');
  const [errors, setErrors] = useState<FormErrors>({});
  const [done, setDone] = useState<string | null>(null);
  const spec = eventSpecs[type];
  const linksToRoot = spec.reference !== undefined && spec.root !== true;
  const rootType: OperationalEventType | null = !linksToRoot ? null : spec.reference === 'supportId' ? 'support_requested' : 'retrospective_action';
  const roots = rootType === null ? [] : events.filter((event) => event.type === rootType);
  const root = roots.find((event) => event.id === rootId);
  const fields = [...spec.required, ...spec.optional].filter((field) => field !== spec.reference);
  // A linked project is preselected only when the API lists it for this account; never assumed.
  const listedProjects = projectList.data?.items;
  const requestedListed = requestedProject !== null && (listedProjects ?? []).some((project) => project.id === requestedProject);
  const requestedMissing = requestedProject !== null && listedProjects !== undefined && !requestedListed;
  const [appliedRequest, setAppliedRequest] = useState(false);
  if (!appliedRequest && requestedListed && requestedProject !== null) {
    setAppliedRequest(true);
    setProjectId(requestedProject);
  }
  const effectiveProject = root !== undefined ? (root.projectId ?? '') : projectId;
  const effectiveVisibility = root !== undefined ? root.visibility : visibility;

  function submit(event: FormEvent) {
    event.preventDefault();
    const full: EventDraft = { ...draft };
    const next: FormErrors = {};
    if (spec.reference !== undefined) {
      if (spec.root === true) full[spec.reference] = newReference();
      else if (root === undefined) next.root = `Choose the ${fieldLabel[spec.reference].toLowerCase()} this follows.`;
      else full[spec.reference] = root.payload[spec.reference] ?? '';
    }
    const result = buildPayload(type, full);
    for (const [key, message] of Object.entries(result.errors)) {
      if (key !== spec.reference) next[key as EventField] = message;
    }
    if (spec.needsProject && effectiveProject === '') next.project = 'Choose the project this belongs to.';
    const at = localToIso(occurredAt);
    if (at === null) next.occurredAt = 'Enter when it happened.';
    setErrors(next);
    if (Object.keys(next).length > 0 || result.payload === null || at === null) return;
    setDone(null);
    record.mutate(
      { type, occurredAt: at, payload: result.payload, visibility: effectiveVisibility, ...(effectiveProject !== '' ? { projectId: effectiveProject } : {}) },
      {
        onSuccess: () => {
          setDone(`${spec.label} recorded.`);
          setDraft({});
          setRootId('');
        },
      },
    );
  }

  const setField = (field: EventField) => (value: string) => setDraft((current) => ({ ...current, [field]: value }));

  return (
    <section className="panel" aria-labelledby="event-title">
      <h2 id="event-title" className="panel__title">
        Record a workflow event
      </h2>
      <p className="panel__lede">
        Each event is an explicit record. Follow-ups are linked by choosing the request or action they belong to. Outcomes offer{' '}
        <strong>{outcomeLabel.unknown}</strong> so an unknown result is never stored as a failure.
      </p>
      <form className="form" onSubmit={submit} noValidate>
        <Field label="Event" help={spec.description}>
          {(props) => (
            <select
              {...props}
              className="input"
              value={type}
              onChange={(event) => {
                setType(event.target.value as OperationalEventType);
                setErrors({});
                setDone(null);
                setRootId('');
              }}
            >
              {types.map((option) => (
                <option key={option} value={option}>
                  {eventSpecs[option].label}
                </option>
              ))}
            </select>
          )}
        </Field>
        {linksToRoot && (
          <Field
            label={spec.reference === 'supportId' ? 'Which support request?' : 'Which retrospective action?'}
            error={errors.root ?? null}
            help={truncated ? 'Showing the most recent 500 events.' : 'Linked events keep the project and visibility of what they follow.'}
          >
            {(props) =>
              roots.length === 0 ? (
                <p id={props.id} className="muted">
                  None recorded yet. Record “{rootType === null ? '' : eventSpecs[rootType].label}” first.
                </p>
              ) : (
                <select {...props} className="input" value={rootId} onChange={(event) => setRootId(event.target.value)}>
                  <option value="">Choose…</option>
                  {roots.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {rootLabel(candidate, projects)}
                    </option>
                  ))}
                </select>
              )
            }
          </Field>
        )}
        {requestedMissing && (
          <p className="notice notice--flag" role="note">
            The linked project ({requestedProject}) isn’t registered for your account yet. Choose a registered project, or record the event
            without one where that’s allowed.
          </p>
        )}
        {root !== undefined ? (
          <p className="small muted">
            Project: {root.projectId ? (projects.get(root.projectId) ?? 'a project you can see') : 'none'} · Visible to: {root.visibility} (from the
            linked {spec.reference === 'supportId' ? 'request' : 'action'})
          </p>
        ) : (
          <Field label={spec.needsProject ? 'Project' : 'Project (optional)'} error={errors.project ?? null}>
            {(props) => (
              <select {...props} className="input" value={projectId} onChange={(event) => setProjectId(event.target.value)}>
                <option value="">{spec.needsProject ? 'Choose a project' : 'No project'}</option>
                {(projectList.data?.items ?? []).map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        {fields.map((field) => {
          const label = spec.optional.includes(field) && field !== 'artifactUrl' ? `${fieldLabel[field]} (optional)` : fieldLabel[field];
          if (field === 'outcome') {
            return (
              <fieldset key={field} className="choice-group choice-group--inline">
                <legend className="field__label">{label}</legend>
                {OUTCOMES.map((outcome) => (
                  <label key={outcome} className={`choice${draft.outcome === outcome ? ' choice--selected' : ''}`}>
                    <input type="radio" name="outcome" checked={draft.outcome === outcome} onChange={() => setField('outcome')(outcome)} />
                    <span className="choice__label">{outcomeLabel[outcome]}</span>
                  </label>
                ))}
                {errors.outcome && <p className="field__error">Choose an outcome — “{outcomeLabel.unknown}” is a valid answer.</p>}
              </fieldset>
            );
          }
          return (
            <Field key={field} label={label} error={errors[field] ?? null}>
              {(props) => {
                if (field === 'note') {
                  return (
                    <textarea {...props} className="input textarea" rows={3} value={draft.note ?? ''} onChange={(event) => setField('note')(event.target.value)} />
                  );
                }
                if (field === 'stage') {
                  return (
                    <select {...props} className="input" value={draft.stage ?? ''} onChange={(event) => setField('stage')(event.target.value)}>
                      <option value="">Choose a stage</option>
                      {stages.map((stage) => (
                        <option key={stage} value={stage}>
                          {stage === 'unknown' ? 'Not known' : stage}
                        </option>
                      ))}
                    </select>
                  );
                }
                const inputType =
                  field === 'windowStart' || field === 'windowEnd' ? 'datetime-local' : field === 'minutes' ? 'number' : field === 'artifactUrl' ? 'url' : 'text';
                return (
                  <input
                    {...props}
                    className="input"
                    type={inputType}
                    {...(field === 'minutes' ? { min: 0, max: 1440 } : {})}
                    value={draft[field] ?? ''}
                    onChange={(event) => setField(field)(event.target.value)}
                  />
                );
              }}
            </Field>
          );
        })}
        <Field label="When it happened (UTC)" error={errors.occurredAt ?? null}>
          {(props) => <input {...props} type="datetime-local" className="input" value={occurredAt} onChange={(event) => setOccurredAt(event.target.value)} />}
        </Field>
        {root === undefined && (
          <Field label="Visible to">
            {(props) => (
              <select {...props} className="input" value={visibility} onChange={(event) => setVisibility(event.target.value as Visibility)}>
                <option value="stewards">Stewards</option>
                <option value="members">Invited members</option>
                {session.hasRole('owner') && <option value="owner">Owner only</option>}
              </select>
            )}
          </Field>
        )}
        <MutationError
          error={record.error}
          conflictMessage="This event was already recorded for that request or action. Record an explicit correction instead."
        />
        {done !== null && (
          <p className="form-success" role="status">
            {done}
          </p>
        )}
        <button type="submit" className="button button--primary" disabled={record.isPending}>
          {record.isPending ? 'Recording…' : 'Record event'}
        </button>
      </form>
    </section>
  );
}

function EventLog({ events, projects }: { events: OperationalEvent[]; projects: Map<string, string> }) {
  if (events.length === 0) return <p className="muted">No workflow events have been recorded.</p>;
  const sorted = [...events].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  return (
    <div className="matrix-wrap" tabIndex={0} role="region" aria-label="Recorded workflow events">
      <table className="matrix matrix--log">
        <caption>Recorded workflow events, newest first. Corrected events are replaced by their correction.</caption>
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Event</th>
            <th scope="col">Linked to</th>
            <th scope="col">Outcome</th>
            <th scope="col">Note</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((event) => {
            const root = rootFor(event, events);
            return (
              <tr key={event.id}>
                <td>{formatDateTime(event.occurredAt)}</td>
                <td>{eventSpecs[event.type]?.label ?? event.type}</td>
                <td>{root === undefined ? '—' : rootLabel(root, projects)}</td>
                <td>{event.payload.outcome === undefined ? '—' : outcomeLabel[event.payload.outcome]}</td>
                <td>{event.payload.note ?? <span className="muted">Withheld</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Only failed analysis jobs can be retried; running or other jobs never show the form. */
export const isRetryableAnalysis = (job: JobState) => job.type === 'anthropic' && job.status === 'failed';

type RetryOutcome =
  | { kind: 'refused'; status: number; code: string; message: string }
  | { kind: 'unclear'; message: string };

interface RetryReceipt {
  grantId?: string;
  answeredAt: number;
}

/**
 * A 200 can be an idempotent replay of an earlier grant, so it says nothing
 * by itself about whether the job was queued again. The wording follows the
 * job's status as fetched after the answer arrived.
 */
export function describeReceipt(
  receipt: RetryReceipt,
  current: JobState | undefined,
  refreshed: boolean,
  refreshFailed: boolean,
): { text: string; allowNew: boolean } {
  const grant = receipt.grantId !== undefined ? ` (grant ${receipt.grantId.slice(0, 8)}…)` : '';
  if (!refreshed) {
    return refreshFailed
      ? { text: `The server returned a grant${grant}, but the job’s current status could not be refreshed, so it is unknown whether anything was queued.`, allowNew: false }
      : { text: `The server returned a grant${grant}. Checking the job’s current status…`, allowNew: false };
  }
  if (current === undefined) {
    return { text: `The server returned a grant${grant}, but this job is no longer listed, so its current status is unknown.`, allowNew: false };
  }
  if (current.status === 'pending' || current.status === 'running') {
    return {
      text: `Retry granted${grant}. The job is ${current.status === 'running' ? 'running' : 'queued'} again. The budget is still checked before any paid request, so this does not mean the analysis will run or succeed.`,
      allowNew: false,
    };
  }
  if (current.status === 'failed') {
    return {
      text: `The server returned a grant${grant}, but the job is failed now. This can be an earlier grant that was already used, so nothing new was queued by this answer. You can make a new request.`,
      allowNew: true,
    };
  }
  return { text: `The server returned a grant${grant}. The job’s current status is “${current.status}”, so nothing new was queued by this answer.`, allowNew: false };
}

/**
 * Asks for one audited retry grant. Nothing is sent automatically. A person's
 * attempt carries one UUID: after an unclear outcome (network failure or
 * server error) the same request is resent unchanged with the same key, so
 * the server returns the original grant rather than creating a second one. A
 * definitive answer ends the attempt; a later, deliberate request gets a new key.
 */
function RetryJob({ job }: { job: JobState }) {
  const retry = useRetryAnalysisJob(job.id);
  const operations = useOperations(true);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [attempt, setAttempt] = useState<{ key: string; reason: string } | null>(null);
  const [outcome, setOutcome] = useState<RetryOutcome | null>(null);
  const [receipt, setReceipt] = useState<RetryReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const unclear = outcome?.kind === 'unclear';

  function submit(event: FormEvent) {
    event.preventDefault();
    const text = reason.trim();
    if (text === '') return setError('Give a reason. It is recorded with the audited grant.');
    if (text.length > 1000) return setError('Keep the reason to 1,000 characters or fewer.');
    setError(null);
    const current = attempt ?? { key: newReference(), reason: text };
    setAttempt(current);
    retry.mutate(
      { reason: current.reason, idempotencyKey: current.key },
      {
        onSuccess: ({ grant, answeredAt }) => {
          setReceipt({ answeredAt, ...(typeof grant?.grantId === 'string' ? { grantId: grant.grantId } : {}) });
          setOutcome(null);
          setAttempt(null);
          setOpen(false);
          setReason('');
        },
        onError: (failure) => {
          if (failure instanceof ApiError && failure.status >= 400 && failure.status < 500) {
            setOutcome({ kind: 'refused', status: failure.status, code: failure.code, message: failure.message });
            setAttempt(null);
          } else {
            setOutcome({ kind: 'unclear', message: failure instanceof ApiError ? failure.message : 'No answer was received.' });
          }
        },
      },
    );
  }

  const current = operations.data?.jobs?.find((candidate) => candidate.id === job.id);
  const refreshed = receipt !== null && operations.dataUpdatedAt >= receipt.answeredAt && !operations.isFetching;
  const refreshFailed = receipt !== null && operations.errorUpdatedAt >= receipt.answeredAt;
  const described = receipt === null ? null : describeReceipt(receipt, current, refreshed, refreshFailed);
  const canRequest = described === null ? job.status === 'failed' : described.allowNew;

  if (!canRequest && described === null && !open) return null;

  return (
    <div className="job-retry">
      {described !== null && !open && (
        <p className={described.allowNew ? 'notice notice--flag' : 'form-success'} role="status">
          {described.text}
        </p>
      )}
      {!open ? (
        canRequest && (
          <button
            type="button"
            className="button button--quiet"
            aria-label={receipt === null ? `Request a retry for job ${job.id}` : `Request a new retry for job ${job.id}`}
            onClick={() => {
              setReceipt(null);
              setOutcome(null);
              setOpen(true);
            }}
          >
            {receipt === null ? 'Request a retry' : 'Make a new request'}
          </button>
        )
      ) : (
        <form className="form" onSubmit={submit} noValidate aria-label={`Retry request for job ${job.id}`}>
          <Field
            label="Reason for this retry"
            error={error}
            help="Recorded with an audited grant. At most three retries per analysis input each UTC month."
          >
            {(props) => (
              <textarea
                {...props}
                className="input textarea"
                rows={3}
                maxLength={1000}
                // Frozen while a request is in flight or its outcome is unclear, and showing the
                // captured reason, so what is on screen is exactly what is (re)sent.
                readOnly={retry.isPending || unclear}
                value={attempt !== null ? attempt.reason : reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
          {outcome?.kind === 'refused' && (
            <p className="form-error" role="alert">
              The retry was not granted ({outcome.code}): {outcome.message}
            </p>
          )}
          {unclear && (
            <p className="form-error" role="alert">
              No clear answer came back ({outcome.message}). The request may have reached the server. Resend it unchanged: it carries the same
              attempt ID, so it cannot create a second grant.
            </p>
          )}
          <div className="button-row">
            <button type="submit" className="button button--primary" disabled={retry.isPending}>
              {retry.isPending ? 'Requesting…' : unclear ? 'Resend the same request' : 'Request retry'}
            </button>
            {!unclear && (
              <button
                type="button"
                className="button button--quiet"
                onClick={() => {
                  setOpen(false);
                  setOutcome(null);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

function CollectionState({ data }: { data: OperationsResponse }) {
  const budget = data.budget;
  return (
    <section className="panel" aria-labelledby="collection-title">
      <h2 id="collection-title" className="panel__title">
        Collection and analysis budget
      </h2>
      {budget === undefined || budget === null ? (
        <p className="muted">
          Budget state is not reported yet. External analysis stays limited to $50 per UTC month and pauses before the limit; this panel shows the
          real ledger once the collection worker reports it.
        </p>
      ) : (
        <div className="budget">
          <p>
            {budget.month}: <strong>${budget.spentUsd.toFixed(2)}</strong> spent and ${budget.reservedUsd.toFixed(2)} reserved of $
            {budget.limitUsd.toFixed(2)}.
          </p>
          <div
            className="meter"
            role="meter"
            aria-label="Analysis budget used or reserved"
            aria-valuemin={0}
            aria-valuemax={budget.limitUsd}
            aria-valuenow={budget.spentUsd + budget.reservedUsd}
          >
            <span className="meter__spent" style={{ width: `${String(Math.min(100, (budget.spentUsd / budget.limitUsd) * 100))}%` }} />
            <span className="meter__reserved" style={{ width: `${String(Math.min(100, (budget.reservedUsd / budget.limitUsd) * 100))}%` }} />
          </div>
          {budget.paused && (
            <p className="notice notice--contested">
              Analysis is paused{budget.pauseReason ? `: ${budget.pauseReason}` : ''}. Collection and browsing continue.
            </p>
          )}
        </div>
      )}
      {(data.acquisitions ?? []).length > 0 && (
        <div className="matrix-wrap" tabIndex={0} role="region" aria-label="Acquisition state">
          <table className="matrix matrix--log">
            <caption>Recent authorized acquisitions.</caption>
            <thead>
              <tr>
                <th scope="col">Scope</th>
                <th scope="col">Status</th>
                <th scope="col">Last run</th>
                <th scope="col">Failures</th>
              </tr>
            </thead>
            <tbody>
              {(data.acquisitions ?? []).map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.sourceKind}: {item.scope}
                  </td>
                  <td>{item.status}</td>
                  <td>{formatDateTime(item.lastRunAt ?? item.lastSuccessAt)}</td>
                  <td>
                    {item.failures}
                    {item.lastError ? ` — ${item.lastError}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(data.jobs ?? []).length > 0 && (
        <ul className="point-list">
          {(data.jobs ?? []).map((job) => (
            <li key={job.id}>
              {job.type}: {job.status} after {job.attempts} attempt{job.attempts === 1 ? '' : 's'}
              {job.nextRunAt ? `, next ${formatDateTime(job.nextRunAt)}` : ''}
              {job.lastError ? ` — ${job.lastError}` : ''}
              {job.type === 'anthropic' && <RetryJob job={job} />}
            </li>
          ))}
        </ul>
      )}
      {(data.integrations ?? []).length > 0 && (
        <>
          <p className="minor-title">Integrations</p>
          <ul className="point-list">
            {(data.integrations ?? []).map((item) => (
              <li key={item.name}>
                {item.name.replace(/_/g, ' ')}: {item.available ? 'connected' : 'not yet installed'}
              </li>
            ))}
          </ul>
        </>
      )}
      {(data.limitations ?? []).length > 0 && (
        <ul className="point-list point-list--limits">
          {(data.limitations ?? []).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function InvitationRow({ invitation }: { invitation: Invitation }) {
  const session = useSession();
  const update = useSetInvitation();
  const [confirming, setConfirming] = useState(false);
  const locked = invitation.role === 'owner' && !session.hasRole('owner');
  const set = (active: boolean) =>
    update.mutate({ discordId: invitation.discordId, role: invitation.role, active }, { onSuccess: () => setConfirming(false) });
  return (
    <li className="invitation">
      <span>
        <span className="mono">{invitation.discordId}</span> · {roleLabel[invitation.role]} ·{' '}
        {invitation.active ? 'Active' : <span className="muted">Revoked</span>}
      </span>
      {locked ? (
        <span className="small muted">Only an owner can change owner access</span>
      ) : invitation.active ? (
        confirming ? (
          <span className="button-row">
            <button
              type="button"
              className="button button--primary"
              aria-label={`Confirm revoke for ${invitation.discordId}`}
              disabled={update.isPending}
              onClick={() => set(false)}
            >
              Confirm revoke
            </button>
            <button type="button" className="button button--quiet" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="button button--quiet"
            aria-label={`Revoke access for ${invitation.discordId}`}
            onClick={() => setConfirming(true)}
          >
            Revoke access
          </button>
        )
      ) : (
        <button
          type="button"
          className="button button--quiet"
          aria-label={`Reinstate ${invitation.discordId}`}
          disabled={update.isPending}
          onClick={() => set(true)}
        >
          Reinstate
        </button>
      )}
      <MutationError error={update.error} conflictMessage="The last active owner cannot be removed, or this invitation changed. Reload and check." />
    </li>
  );
}

function Invitations() {
  const session = useSession();
  const list = useInvitations();
  const invite = useSetInvitation();
  const [discordId, setDiscordId] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const items = list.data?.pages.flatMap((page) => page.items) ?? [];

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!/^\d{5,30}$/.test(discordId.trim())) {
      setError('Enter the numeric Discord account ID (not a username).');
      return;
    }
    setError(null);
    setDone(null);
    invite.mutate(
      { discordId: discordId.trim(), role, active: true },
      {
        onSuccess: () => {
          setDone('Invitation saved.');
          setDiscordId('');
        },
      },
    );
  }

  return (
    <section className="panel" aria-labelledby="invite-title">
      <h2 id="invite-title" className="panel__title">
        Invitations
      </h2>
      {list.isPending && <Loading label="Loading invitations" />}
      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}
      {list.data !== undefined &&
        (items.length === 0 ? (
          <p className="muted">No invitations are recorded.</p>
        ) : (
          <ul className="invitation-list">
            {items.map((invitation) => (
              <InvitationRow key={invitation.discordId} invitation={invitation} />
            ))}
          </ul>
        ))}
      {list.hasNextPage && (
        <button type="button" className="button button--secondary" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
          Load more invitations
        </button>
      )}
      <form className="form" onSubmit={submit} noValidate>
        <Field label="Discord account ID" error={error} help="Only invited Discord IDs can sign in. Revoking access also ends their sessions.">
          {(props) => (
            <input {...props} className="input mono" inputMode="numeric" value={discordId} onChange={(event) => setDiscordId(event.target.value)} />
          )}
        </Field>
        <Field label="Role">
          {(props) => (
            <select {...props} className="input" value={role} onChange={(event) => setRole(event.target.value as Role)}>
              <option value="member">Member</option>
              <option value="steward">Steward</option>
              {session.hasRole('owner') && <option value="owner">Owner</option>}
            </select>
          )}
        </Field>
        <MutationError error={invite.error} />
        {done !== null && (
          <p className="form-success" role="status">
            {done}
          </p>
        )}
        <button type="submit" className="button button--primary" disabled={invite.isPending}>
          {invite.isPending ? 'Saving…' : 'Save invitation'}
        </button>
      </form>
    </section>
  );
}

export function Operations() {
  const operations = useOperations(true);
  const events = useEvents({});
  const projects = useProjects();
  const projectTitles = new Map((projects.data?.items ?? []).map((project) => [project.id, project.title]));
  return (
    <main id="main" tabIndex={-1} className="page page--wide">
      <header className="page__header">
        <p className="eyebrow">Stewardship</p>
        <h1 className="page__title">Measurement and operations</h1>
        <p className="lede">
          Record prospective workflow events, follow requests and actions through to an outcome, watch collection and the analysis budget, and
          manage invitations. Only what the API reports is shown; nothing is estimated.
        </p>
      </header>
      <StewardNav />
      <div className="operations">
        <div className="operations__main">
          {events.isPending && <Loading label="Loading workflow events" />}
          {events.isError && <ErrorState error={events.error} onRetry={() => void events.refetch()} />}
          {events.data !== undefined && (
            <>
              <MeasurementStatus events={events.data.items} />
              <FollowUps events={events.data.items} projects={projectTitles} />
              <EventForm events={events.data.items} projects={projectTitles} truncated={events.data.nextCursor !== undefined && events.data.nextCursor !== null} />
              <section className="panel" aria-labelledby="log-title">
                <h2 id="log-title" className="panel__title">
                  Event log
                </h2>
                <EventLog events={events.data.items} projects={projectTitles} />
              </section>
            </>
          )}
        </div>
        <div className="operations__side">
          <Metrics />
          {operations.isPending && <Loading label="Loading collection state" />}
          {operations.isError && <ErrorState error={operations.error} onRetry={() => void operations.refetch()} />}
          {operations.data !== undefined && <CollectionState data={operations.data} />}
          <Invitations />
        </div>
      </div>
    </main>
  );
}
